/**
 * Suppliers Dual-Write Test Suite
 *
 * This utility automatically creates suppliers with various configurations
 * and verifies that Supabase and Firestore are in sync.
 *
 * Usage:
 *   import { runSuppliersDualWriteTests } from './utils/suppliersDualWriteTest';
 *   await runSuppliersDualWriteTests(db, testConfig);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import {
  saveNewSupplier,
  updateSupplier,
  deleteSupplier,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { suppliersApi } from '../global/API/typedApiClient';
import { getDefaultStore } from 'jotai';
import {
  organizationAtom,
  dbAtom,
  userIdAtom,
  storesDictAtom,
} from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * All test variations to run
 */
export const SUPPLIER_TEST_VARIATIONS = [
  // Basic CRUD (S1-S2)
  { id: 'S1', category: 'basic', name: 'Create with required fields only' },
  { id: 'S2', category: 'basic', name: 'Create with all fields' },

  // Update (S3)
  { id: 'S3', category: 'lifecycle', name: 'Update supplier', lifecycleAction: 'update' },

  // Delete (S4)
  { id: 'S4', category: 'lifecycle', name: 'Delete supplier', lifecycleAction: 'delete' },
];

/**
 * Format date for dailyStatID (d-M-y format)
 */
function formatDateForDailyStat(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

/**
 * Generate test supplier data for a specific variation
 */
export function generateTestSupplier(variation, testData) {
  const { organizationId, storeId } = testData;

  // Base supplier data
  const now = new Date();
  const timestamp = Date.now();

  const baseSupplier = {
    organizationId,
    name: `Test Supplier ${variation.id} - ${timestamp}`,
    phone: null,
    email: null,
    address: null,
    notes: null,
    lastChangeAt: now,
    lastChangeStore: storeId,
    dailyStatID: `test-${formatDateForDailyStat(now)}-${storeId}`,
  };

  // Apply variation
  switch (variation.category) {
    case 'basic':
      return applyBasicVariation(baseSupplier, variation, testData);
    case 'lifecycle':
      return applyLifecycleVariation(baseSupplier, variation, testData);
    default:
      return baseSupplier;
  }
}

function applyBasicVariation(supplier, variation, testData) {
  switch (variation.id) {
    case 'S1': // Required fields only
      supplier.name = `Test Basic Supplier ${Date.now()}`;
      break;

    case 'S2': // All fields
      supplier.name = `Test Full Supplier ${Date.now()}`;
      supplier.phone = '+1234567890';
      supplier.email = 'test@supplier.com';
      supplier.address = '123 Test Street';
      supplier.notes = 'Test notes for supplier';
      break;
  }

  return supplier;
}

function applyLifecycleVariation(supplier, variation, testData) {
  // Start with a basic supplier that can be modified
  supplier.name = `Test Lifecycle Supplier ${variation.id} - ${Date.now()}`;

  // Store lifecycle action metadata
  supplier._lifecycleAction = variation.lifecycleAction;
  supplier._lifecycleData = {};

  switch (variation.id) {
    case 'S3': // Update
      supplier._lifecycleData = {
        updateFields: true,
        updateInfo: {
          name: `Updated Supplier Name ${Date.now()}`,
          phone: '+9876543210',
          notes: 'Updated notes',
        },
      };
      break;

    case 'S4': // Delete
      supplier._lifecycleData = {
        deleteSupplier: true,
      };
      break;
  }

  return supplier;
}

/**
 * Execute lifecycle action after supplier creation
 */
async function executeLifecycleAction(db, supplierRef, supplierData, testData) {
  const action = supplierData._lifecycleAction;
  const data = supplierData._lifecycleData;

  if (!action || !data) return { actionPerformed: null };

  // Handle skip case
  if (data.skip) {
    return { actionPerformed: 'skip', skipReason: data.skipReason };
  }

  // Wait a bit for initial sync
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    if (data.updateFields) {
      // Update supplier via updateSupplier
      await updateSupplier(supplierRef, data.updateInfo);
      return { actionPerformed: 'updateFields' };
    }

    if (data.deleteSupplier) {
      // Delete with empty products array (no products to reassign)
      await deleteSupplier(supplierRef, [], testData.stores);
      return { actionPerformed: 'delete' };
    }
  } catch (error) {
    console.error('Lifecycle action failed:', error.message);
    return { actionPerformed: action, error: error.message };
  }

  return { actionPerformed: null };
}

/**
 * Run a single test variation
 */
async function runSingleTest(db, variation, testData, options = {}) {
  const { cleanup = true, delayMs = 5000 } = options;

  console.log(`\n--- Running test ${variation.id}: ${variation.name} ---`);

  try {
    // Generate test supplier
    const supplierData = generateTestSupplier(variation, testData);

    // Check if test should be skipped
    if (supplierData._lifecycleData?.skip) {
      console.log(`SKIP: ${variation.name} - ${supplierData._lifecycleData.skipReason}`);
      testResults.skipped.push({ variation, reason: supplierData._lifecycleData.skipReason });
      return null;
    }

    console.log(`Creating supplier...`);

    // Create supplier via frontend code (this triggers dual-write)
    const result = await saveNewSupplier(db, supplierData);
    const firestoreId = result.id;
    const supplierRef = db.collection('suppliers').doc(firestoreId);

    console.log(`Created supplier: ${firestoreId}`);

    // Wait for Supabase write to complete (CRITICAL: 5 second wait)
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // For lifecycle tests, perform the action after initial creation
    let lifecycleResult = null;
    if (variation.category === 'lifecycle') {
      console.log(`Performing lifecycle action: ${variation.lifecycleAction}`);
      lifecycleResult = await executeLifecycleAction(db, supplierRef, supplierData, testData);
      console.log(`Lifecycle action result:`, lifecycleResult);

      // Handle skipped lifecycle tests
      if (lifecycleResult?.actionPerformed === 'skip') {
        console.log(`SKIP: ${variation.name} - ${lifecycleResult.skipReason}`);
        testResults.skipped.push({ variation, reason: lifecycleResult.skipReason });
        // Cleanup the created supplier
        if (cleanup) {
          try {
            await deleteSupplier(supplierRef, [], testData.stores);
          } catch (e) { /* ignore */ }
        }
        return null;
      }

      // Wait for the update to sync (CRITICAL: 5 second wait)
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    // Compare using the API
    let comparisonResult;

    // Special case: if supplier was deleted, verify it's gone from both DBs
    if (lifecycleResult?.actionPerformed === 'delete') {
      comparisonResult = await verifyDeletion(firestoreId, supplierRef, db);
    } else {
      comparisonResult = await compareViaApi(firestoreId, supplierRef, db, supplierData);
    }

    // Log result
    if (comparisonResult.matches) {
      console.log(`PASS: ${variation.name}`);
      testResults.passed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    } else {
      console.log(`FAIL: ${variation.name}`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparisonResult.differences), null, 2));
      testResults.failed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    }

    // Cleanup - delete the test supplier (skip if already deleted)
    if (cleanup && lifecycleResult?.actionPerformed !== 'delete') {
      try {
        const supplierDoc = await supplierRef.get();
        if (supplierDoc.exists) {
          await deleteSupplier(supplierRef, [], testData.stores);
          console.log(`Cleaned up supplier: ${firestoreId}`);
        }
      } catch (cleanupError) {
        console.warn(`Failed to cleanup supplier ${firestoreId}:`, cleanupError.message);
      }
    }

    return comparisonResult;
  } catch (error) {
    console.error(`ERROR in test ${variation.id}:`, error.message);
    console.error(`Stack trace:`, error.stack);
    testResults.failed.push({
      variation,
      error: error.message,
      stack: error.stack,
    });
    return null;
  }
}

/**
 * Compare supplier data via backend API
 */
async function compareViaApi(firestoreId, supplierRef, db, originalSupplierData) {
  try {
    const response = await api.get(`/suppliers/compare/${firestoreId}`);
    const apiResult = response.data;

    // Convert API result to our comparison format
    if (!apiResult.exists) {
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: true,
        matches: false,
        differences: [{
          field: 'supplier',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    // Get Firestore data for comparison
    let firestoreData = null;
    try {
      const doc = await supplierRef.get();
      if (doc.exists) {
        firestoreData = { id: doc.id, ...doc.data() };
      }
    } catch (error) {
      console.warn(`Could not fetch from Firestore: ${error.message}`);
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare fields
    const fieldsToCompare = [
      { supabase: 'name', firestore: 'name' },
      { supabase: 'phone', firestore: 'phone' },
      { supabase: 'email', firestore: 'email' },
      { supabase: 'address', firestore: 'address' },
      { supabase: 'notes', firestore: 'notes' },
    ];

    for (const field of fieldsToCompare) {
      let supabaseValue = supabaseData?.[field.supabase];
      let firestoreValue = firestoreData?.[field.firestore];

      // Normalize values
      supabaseValue = normalizeValue(supabaseValue);
      firestoreValue = normalizeValue(firestoreValue);

      if (!valuesMatch(supabaseValue, firestoreValue)) {
        differences.push({
          field: field.supabase,
          supabaseValue,
          firestoreValue,
          severity: 'critical',
        });
      }
    }

    return {
      firestoreId,
      foundInSupabase: true,
      foundInFirestore: !!firestoreData,
      matches: differences.length === 0,
      differences,
      warnings: [],
      supabaseData,
      firestoreData,
    };
  } catch (error) {
    console.warn('API comparison failed:', error.message);
    return {
      firestoreId,
      foundInSupabase: false,
      foundInFirestore: true,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: error.message,
        firestoreValue: 'exists',
        severity: 'critical',
      }],
      warnings: [error.message],
    };
  }
}

/**
 * Verify that a supplier has been deleted from both databases
 */
async function verifyDeletion(firestoreId, supplierRef, db) {
  const maxRetries = 15;
  const retryDelayMs = 2000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const differences = [];
    let firestoreExists = false;
    let supabaseExists = false;

    // Check Firestore
    try {
      const firestoreDoc = await supplierRef.get();
      firestoreExists = firestoreDoc.exists;
    } catch (error) {
      firestoreExists = false;
    }

    // Check Supabase via compare endpoint
    try {
      const response = await api.get(`/suppliers/compare/${firestoreId}`);
      supabaseExists = response.data?.exists || false;
    } catch (error) {
      // 404 or other error means not found
      supabaseExists = false;
    }

    // Both deleted - success!
    if (!firestoreExists && !supabaseExists) {
      console.log(`Delete verified on attempt ${attempt}: both databases show deleted`);
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: false,
        matches: true,
        differences: [],
        warnings: [],
      };
    }

    // Build differences for logging
    if (firestoreExists) {
      differences.push({
        field: 'firestore_deletion',
        supabaseValue: 'deleted',
        firestoreValue: 'still exists',
        severity: 'critical',
      });
    }
    if (supabaseExists) {
      differences.push({
        field: 'supabase_deletion',
        supabaseValue: 'still exists',
        firestoreValue: 'deleted',
        severity: 'critical',
      });
    }

    // If last attempt, return failure
    if (attempt === maxRetries) {
      console.log(`Delete verification failed after ${maxRetries} attempts`);
      return {
        firestoreId,
        foundInSupabase: supabaseExists,
        foundInFirestore: firestoreExists,
        matches: false,
        differences,
        warnings: [`Deletion not complete after ${maxRetries} retries`],
      };
    }

    // Wait and retry
    console.log(`Delete verification attempt ${attempt}/${maxRetries}: waiting...`);
    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
  }

  return {
    firestoreId,
    foundInSupabase: true,
    foundInFirestore: true,
    matches: false,
    differences: [],
    warnings: ['Unexpected state in verifyDeletion'],
  };
}

function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  if (value === '') return null;
  if (typeof value === 'number') return Number(value);
  if (typeof value === 'boolean') return value;
  return value;
}

function valuesMatch(a, b) {
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.01;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
}

/**
 * Run all test variations
 */
export async function runSuppliersDualWriteTests(passedDb, options = {}) {
  const {
    variations = SUPPLIER_TEST_VARIATIONS,
    cleanup = true,
    delayMs = 5000,
    stopOnFailure = false,
  } = options;

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const userId = jotaiStore.get(userIdAtom);

  // Get db from atom if not passed
  const db = passedDb || jotaiStore.get(dbAtom);

  if (!db) {
    throw new Error('Database not initialized. Please wait for app to fully load.');
  }

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!storesDict || Object.keys(storesDict).length === 0) {
    throw new Error('Stores not loaded. Please wait for app to fully load.');
  }

  // Get first store
  const storeId = Object.keys(storesDict)[0];
  if (!storeId) {
    throw new Error('No stores found. Please create a store first.');
  }

  const store = storesDict[storeId];

  const testData = {
    organizationId: organization.id,
    storeId,
    store,
    stores: storesDict,
    uid: userId || 'test-uid',
  };

  console.log('\n========================================');
  console.log('SUPPLIERS DUAL-WRITE TEST SUITE');
  console.log('========================================');
  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Store: ${store.name || storeId}`);
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    const result = await runSingleTest(db, variation, testData, { cleanup, delayMs });

    if (stopOnFailure && result && !result.matches) {
      console.log('\nStopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Print summary
  console.log('\n========================================');
  console.log('TEST SUMMARY');
  console.log('========================================');
  console.log(`Passed: ${testResults.passed.length}`);
  console.log(`Failed: ${testResults.failed.length}`);
  console.log(`Skipped: ${testResults.skipped.length}`);
  console.log('========================================\n');

  if (testResults.failed.length > 0) {
    console.log('FAILED TESTS:');
    testResults.failed.forEach(({ variation, result, error }) => {
      console.log(`  - ${variation.id}: ${variation.name}`);
      if (error) {
        console.log(`    Error: ${error}`);
      } else if (result?.differences) {
        result.differences.forEach(diff => {
          console.log(`    ${diff.field}: Supabase=${JSON.stringify(sanitizeForFirestore(diff.supabaseValue))} vs Firestore=${JSON.stringify(sanitizeForFirestore(diff.firestoreValue))}`);
        });
      }
    });
  }

  return testResults;
}

/**
 * Quick smoke test - runs only the first 2 basic tests
 */
export async function runQuickSuppliersSmokeTest(passedDb, options = {}) {
  const quickVariations = SUPPLIER_TEST_VARIATIONS.filter(v =>
    v.id === 'S1' || v.id === 'S2'
  );

  return runSuppliersDualWriteTests(passedDb, {
    ...options,
    variations: quickVariations,
  });
}

export default {
  runSuppliersDualWriteTests,
  generateTestSupplier,
  SUPPLIER_TEST_VARIATIONS,
};
